import { useCallback, useState } from 'react';
import { DragEndEvent, DragStartEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { TFunction } from 'i18next';

import { DropQuantityRow } from '../components/DropQuantityDialog/DropQuantityDialog';
import { getDialogLineItem } from '../helpers/lineItemDisplay';
import { placedKey } from '../helpers/placedKey';
import { parseDropTarget } from '../helpers/resolveDrop';
import { compareTransportUnitPositions } from '../helpers/transportUnitPositions';
import { PackingAction, UnitTypeOption } from '../state/packingListReducer';
import { LineItemId, PackingDragData, PackingListTree, TransportUnitId } from '../types';
import { useCheckboxSelection } from './useCheckboxSelection';

// A drag always operates on a SET of items — the selection when the dragged item is checked,
// otherwise just that one item (a single drag is a set of one). `fromTuId` is set for placed
// items (a move) and undefined for left-panel products (a placement).
export type DropEntry = DropQuantityRow & { lineItemId: LineItemId; fromTuId?: string };
export type DropRequest = {
  over: string;
  title: string;
  confirmLabel: string;
  usingSelection: boolean;
  entries: Array<DropEntry>;
};

export const usePackingDragAndDrop = (params: {
  packingList: PackingListTree;
  quantityFieldPath: string;
  productSelection: ReturnType<typeof useCheckboxSelection>;
  placedSelection: ReturnType<typeof useCheckboxSelection>;
  remainingByLineItem: Map<LineItemId, number>;
  placedContentByKey: Map<string, { tuId: TransportUnitId; lineItemId: LineItemId; quantity: number }>;
  tuPositionById: Map<TransportUnitId, string>;
  dispatchAndMark: (action: PackingAction) => void;
  unitTypeOptions: Array<UnitTypeOption>;
  openBulkDrop: (request: DropRequest) => void;
  t: TFunction;
}) => {
  const {
    packingList,
    productSelection,
    placedSelection,
    remainingByLineItem,
    placedContentByKey,
    tuPositionById,
    dispatchAndMark,
    unitTypeOptions,
    openBulkDrop,
    t,
  } = params;
  const [beingDraggedId, setBeingDraggedId] = useState<LineItemId | null>(null);
  const [skipDropAnimation, setSkipDropAnimation] = useState(false);
  const [draggedCount, setDraggedCount] = useState(1);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const beingDraggedEntry = beingDraggedId ? packingList.lineItems[beingDraggedId] : null;

  // Builds the drop entries for a set of unplaced products (a placement). Each entry carries its
  // placeable max (remaining to place); items with nothing left to place are dropped. Ordered like
  // the left panel (line-item order) rather than by the order checkboxes were ticked.
  const mapProductEntries = useCallback(
    (ids: Array<LineItemId>): Array<DropEntry> =>
      ids
        .map((id) => ({
          key: id,
          lineItemId: id,
          max: remainingByLineItem.get(id) ?? 0,
          ...getDialogLineItem(packingList.lineItems[id], t),
        }))
        .filter((entry) => entry.max > 0)
        .sort(
          (a, b) =>
            (packingList.lineItems[a.lineItemId]?.originalIndex ?? 0) -
            (packingList.lineItems[b.lineItemId]?.originalIndex ?? 0),
        ),
    [remainingByLineItem, packingList.lineItems, t],
  );

  // Builds the drop entries for a set of placed chips (a move). Each entry carries its source unit,
  // its placement quantity as the max, and a label naming the unit it moves from. Ordered by
  // transport-unit position (then line-item order within a unit) so the dialog reads top-to-bottom
  // like the right panel.
  const mapPlacedEntries = useCallback(
    (keys: Array<string>): Array<DropEntry> =>
      keys
        .map((key) => placedContentByKey.get(key))
        .filter((content): content is { tuId: TransportUnitId; lineItemId: LineItemId; quantity: number } =>
          Boolean(content),
        )
        .map((content) => {
          const position = tuPositionById.get(content.tuId);
          return {
            key: placedKey(content.tuId, content.lineItemId),
            lineItemId: content.lineItemId,
            fromTuId: content.tuId,
            max: content.quantity,
            sourceLabel: position ? t('webedi:packing_list.transport_unit.title', { position }) : undefined,
            ...getDialogLineItem(packingList.lineItems[content.lineItemId], t),
          };
        })
        .sort((a, b) => {
          const byPosition = compareTransportUnitPositions(
            tuPositionById.get(a.fromTuId as string) ?? '',
            tuPositionById.get(b.fromTuId as string) ?? '',
          );
          if (byPosition !== 0) {
            return byPosition;
          }
          return (
            (packingList.lineItems[a.lineItemId]?.originalIndex ?? 0) -
            (packingList.lineItems[b.lineItemId]?.originalIndex ?? 0)
          );
        }),
    [placedContentByKey, packingList.lineItems, tuPositionById, t],
  );

  const moveItem = useCallback(
    (lineItemId: LineItemId, fromTuId: string, toTuId: string, quantity: number) => {
      dispatchAndMark({ type: 'MOVE_ITEM', lineItemId, fromTuId, toTuId, quantity });
    },
    [dispatchAndMark],
  );

  // Apply a drop of one or more entries onto `over`. Each entry is a move when it carries a
  // `fromTuId` (a placed chip) or a placement otherwise (an unplaced product), so a mixed batch of
  // both kinds is handled uniformly. Into an existing unit → MOVE_ITEM / PLACE_ITEM each (a move
  // into the unit the item already sits in is skipped). Onto a new-unit zone → create ONE unit
  // holding every entry: decrement each moved item's source to its leftover first (so a sub-unit
  // created on a source unit doesn't re-spill the moved item), then create the unit with the first
  // entry and place the rest.
  const applyDrop = useCallback(
    (entries: Array<DropEntry & { quantity: number }>, over: string) => {
      const dropTarget = parseDropTarget(over);
      if (!dropTarget) {
        return;
      }
      const valid = entries.filter((entry) => entry.quantity > 0);
      if (valid.length === 0) {
        return;
      }
      if (dropTarget.kind === 'add-item') {
        valid.forEach((entry) => {
          if (entry.fromTuId) {
            if (entry.fromTuId !== dropTarget.tuId) {
              moveItem(entry.lineItemId, entry.fromTuId, dropTarget.tuId, entry.quantity);
            }
            return;
          }
          dispatchAndMark({
            type: 'PLACE_ITEM',
            lineItemId: entry.lineItemId,
            tuId: dropTarget.tuId,
            quantity: entry.quantity,
          });
        });
        return;
      }
      valid
        .filter((entry) => entry.fromTuId)
        .forEach((entry) =>
          dispatchAndMark({
            type: 'SET_ITEM_QUANTITY',
            tuId: entry.fromTuId as string,
            lineItemId: entry.lineItemId,
            quantity: entry.max - entry.quantity,
          }),
        );
      const [first, ...rest] = valid;
      const tuId = crypto.randomUUID();
      if (dropTarget.kind === 'new-tu') {
        dispatchAndMark({
          type: 'CREATE_TU_WITH_ITEM',
          lineItemId: first.lineItemId,
          tuId,
          unitTypeOptions,
          quantity: first.quantity,
        });
      } else {
        // The reducer resolves the actual type from the parent it already has in hand — see
        // CREATE_SUB_UNIT.
        dispatchAndMark({
          type: 'CREATE_SUB_UNIT',
          parentTuId: dropTarget.tuId,
          lineItemId: first.lineItemId,
          childTuId: crypto.randomUUID(),
          tuId,
          unitTypeOptions,
          quantity: first.quantity,
        });
      }
      rest.forEach((entry) =>
        dispatchAndMark({ type: 'PLACE_ITEM', lineItemId: entry.lineItemId, tuId, quantity: entry.quantity }),
      );
    },
    [moveItem, dispatchAndMark, unitTypeOptions],
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current as PackingDragData | undefined;
      // A drag carries the whole selection (both panels) when the dragged item is itself checked and
      // more than one item is selected in total.
      const totalSelected = productSelection.selectedIds.size + placedSelection.selectedIds.size;
      if (!data || data.kind === 'product') {
        const lineItemId = data ? data.lineItemId : String(event.active.id);
        const usingSelection = productSelection.selectedIds.has(lineItemId) && totalSelected > 1;
        setDraggedCount(usingSelection ? totalSelected : 1);
        setBeingDraggedId(lineItemId);
        return;
      }
      const key = placedKey(data.tuId, data.lineItemId);
      const usingSelection = placedSelection.selectedIds.has(key) && totalSelected > 1;
      setDraggedCount(usingSelection ? totalSelected : 1);
      setBeingDraggedId(data.lineItemId);
    },
    [productSelection, placedSelection],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setBeingDraggedId(null);
      setDraggedCount(1);
      const data = event.active.data.current as PackingDragData | undefined;
      const over = event.over ? String(event.over.id) : undefined;
      setSkipDropAnimation(Boolean(over));
      if (!over || !data) {
        return;
      }
      const dropTarget = parseDropTarget(over);
      if (!dropTarget) {
        return;
      }
      const isPlaced = data.kind === 'placed';
      const draggedKey = isPlaced ? placedKey(data.tuId, data.lineItemId) : data.lineItemId;
      const draggedInSelection = isPlaced
        ? placedSelection.selectedIds.has(draggedKey)
        : productSelection.selectedIds.has(draggedKey);
      const totalSelected = productSelection.selectedIds.size + placedSelection.selectedIds.size;
      const usingSelection = draggedInSelection && totalSelected > 1;
      // A selection drag carries every checked item from BOTH panels — products become placements,
      // placed chips become moves; a plain drag carries just the item under the cursor.
      let entries = usingSelection
        ? [
            ...mapProductEntries(Array.from(productSelection.selectedIds)),
            ...mapPlacedEntries(Array.from(placedSelection.selectedIds)),
          ]
        : isPlaced
          ? mapPlacedEntries([draggedKey])
          : mapProductEntries([data.lineItemId]);
      // Moving a placed item into a unit it already sits in is a no-op; products (no fromTuId) stay.
      if (dropTarget.kind === 'add-item') {
        entries = entries.filter((entry) => entry.fromTuId !== dropTarget.tuId);
      }
      if (entries.length === 0) {
        return;
      }
      const hasPlaced = entries.some((entry) => entry.fromTuId);
      const hasProduct = entries.some((entry) => !entry.fromTuId);
      let title = t('webedi:packing_list.drop_dialog.place_title');
      let confirmLabel = t('webedi:packing_list.drop_dialog.add');
      if (hasPlaced && hasProduct) {
        title = t('webedi:packing_list.drop_dialog.mixed_title');
        confirmLabel = t('webedi:packing_list.drop_dialog.mixed');
      } else if (hasPlaced) {
        title = t('webedi:packing_list.drop_dialog.move_title');
        confirmLabel = t('webedi:packing_list.drop_dialog.move');
      }
      // Only pause for the quantity dialog when there's an actual choice to make; a set where every
      // item can only move/place one commits straight away.
      if (entries.some((entry) => entry.max > 1)) {
        openBulkDrop({ over, title, confirmLabel, usingSelection, entries });
        return;
      }
      applyDrop(
        entries.map((entry) => ({ ...entry, quantity: entry.max })),
        over,
      );
      if (usingSelection) {
        productSelection.clear();
        placedSelection.clear();
      }
    },
    [mapPlacedEntries, mapProductEntries, placedSelection, productSelection, applyDrop, openBulkDrop, t],
  );

  const handleDragCancel = useCallback(() => {
    setBeingDraggedId(null);
    setSkipDropAnimation(false);
    setDraggedCount(1);
  }, []);

  return {
    sensors,
    beingDraggedEntry,
    draggedCount,
    skipDropAnimation,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    applyDrop,
  };
};
